'use client';

import { BIAnalyticsService, BISources } from '@terra/ui-telemetry';
import { usePathname } from 'next/navigation';
import { useEffect, useRef } from 'react';
import { useUser } from '../app/providers/auth';
import { useUserTenants } from '../hooks/api/useUserTenants';
import { getConfigValue } from '../lib/public-env/get-config-value';
import { isTerraProxyAuthPublicPathname } from '../middlewares/auth/public-routes';
import { useLayoutStore } from '../store/layoutStore';
import { buildTorchPageViewKey, resolveTorchPageName } from './utils';

export const BIEventsProvider = () => {
  const { user, isUserLoading } = useUser();
  const { currentTenantId, tenants } = useUserTenants();
  const pathname = usePathname() ?? '/';
  const selectedSidebarTab = useLayoutStore(
    (state) => state.selectedSidebarTab,
  );
  const activeTabId = useLayoutStore((state) => state.activeMainContentTabId);
  const tabs = useLayoutStore((state) => state.mainContentTabs);
  const tabNavigationByTabId = useLayoutStore(
    (state) => state.tabNavigationByTabId,
  );
  const previousPageViewKeyRef = useRef<string | null>(null);
  const biAnalyticsService = useRef<BIAnalyticsService | null>(null);
  const tenantName = tenants.find(
    (tenant) => tenant.id === currentTenantId,
  )?.name;

  const activeTab = tabs.find((tab) => tab.id === activeTabId);
  const activeTabNavigation = activeTabId
    ? tabNavigationByTabId[activeTabId]
    : undefined;
  const activePageEntry =
    activeTabNavigation?.entries[activeTabNavigation.currentIndex];

  useEffect(() => {
    const apiKey = getConfigValue('NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN') ?? '';
    const apiHost = getConfigValue('NEXT_PUBLIC_POSTHOG_API_HOST') ?? '';

    if (!apiKey || !apiHost) {
      return;
    }

    biAnalyticsService.current = BIAnalyticsService.getInstance({
      apiKey,
      apiHost,
      source: BISources.TORCH,
    });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    const cleanupLogout = window.electronAPI?.session?.onLoggedOut?.(() => {
      biAnalyticsService.current?.trackLogout();
    });

    return () => {
      cleanupLogout?.();
    };
  }, []);

  useEffect(() => {
    if (currentTenantId) {
      biAnalyticsService.current?.setTenant({
        tenantId: currentTenantId,
        tenantName,
      });
    }
  }, [currentTenantId, tenantName]);

  useEffect(() => {
    const isPublicPath = isTerraProxyAuthPublicPathname(pathname);
    const trackingEnabled =
      !isPublicPath && !isUserLoading && Boolean(user?.userId);

    biAnalyticsService.current?.setTrackingEnabled(trackingEnabled);

    if (!trackingEnabled || !user?.userId) {
      previousPageViewKeyRef.current = null;
      return;
    }

    biAnalyticsService.current?.setUser({
      userId: user.userId,
      email: user.email,
      name: user.name,
    });

    const pageViewContext = {
      sidebarTab: selectedSidebarTab,
      activeTabId,
      activeTabType: activeTab?.type,
      activePageId: activePageEntry?.pageId,
      activePageType: activePageEntry?.type,
      activePageIndex: activeTabNavigation?.currentIndex,
    };
    const pageViewKey = buildTorchPageViewKey(pageViewContext);

    if (pageViewKey === previousPageViewKeyRef.current) {
      return;
    }

    previousPageViewKeyRef.current = pageViewKey;

    const pageName = resolveTorchPageName(pageViewContext);

    biAnalyticsService.current?.trackPageView(pageName);
  }, [
    pathname,
    isUserLoading,
    user,
    selectedSidebarTab,
    activeTabId,
    activeTab?.type,
    activePageEntry?.pageId,
    activePageEntry?.type,
    activeTabNavigation?.currentIndex,
  ]);

  return null;
};
